import React, { useContext, useEffect, useState } from 'react';

import { TaskEntity } from '../../tasks/Task.interface';
import {
  useCreateProductDesignInput,
  useProductEntityPrefixId,
  useProductViewParams,
  useUpdateProductDesignInput
} from '../Product.hooks';
import {
  BaseProductBoardColumnProps,
  ProductDesignInput
} from '../Product.interface';
import { ProductBoardContext } from './ProductBoard.context';
import { ProductBoardColumnEntity } from './ProductBoardColumn';
import ProductBoardItem from './ProductBoardItem';

export type ProductBoardDesignInputProps = BaseProductBoardColumnProps<
  ProductDesignInput,
  'designInput'
>;

const ProductBoardDesignInput: React.FC<ProductBoardDesignInputProps> = (
  props: ProductBoardDesignInputProps
) => {
  const {
    product,
    collapsed,
    designInput: initialDesignInput,
    onCancel,
    onError,
    onSave
  } = props;
  const entity = ProductBoardColumnEntity.DesignInput;
  const params: string = useProductViewParams();
  const { toggleSelected, isSelected } = useContext(ProductBoardContext);
  const [designInput, setDesignInput] = useState(initialDesignInput);
  useEffect(() => {
    if (initialDesignInput) {
      setDesignInput(initialDesignInput);
    }
  }, [initialDesignInput]);
  const {
    createdProductDesignInput,
    isCreatingProductDesignInput,
    createProductDesignInputError,
    createProductDesignInput
  } = useCreateProductDesignInput(product.id);
  const {
    updatedProductDesignInput,
    isUpdatingProductDesignInput,
    updateProductDesignInput
  } = useUpdateProductDesignInput(product.id, designInput.id || 0);
  const entityId: string = useProductEntityPrefixId(
    designInput,
    ProductBoardColumnEntity.DesignInput
  );

  useEffect(() => {
    if (createdProductDesignInput) {
      setDesignInput(createdProductDesignInput);
      onSave?.(createdProductDesignInput, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createdProductDesignInput]);

  useEffect(() => {
    if (updatedProductDesignInput) {
      setDesignInput(updatedProductDesignInput);
      onSave?.(updatedProductDesignInput);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatedProductDesignInput]);

  useEffect(() => {
    if (createProductDesignInputError) {
      onError?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createProductDesignInputError]);

  return (
    <ProductBoardItem
      product={product}
      taskEntity={designInput as TaskEntity}
      title={designInput.title || ''}
      entityId={entityId}
      collapsed={collapsed}
      editable={!designInput.id}
      isSelected={isSelected(designInput.id, entity)}
      loading={isUpdatingProductDesignInput || isCreatingProductDesignInput}
      viewDetailsLink={`/portal/products/${designInput.productId}/design-inputs/${designInput.id}${params}`}
      onSelect={() => toggleSelected(designInput, entity)}
      onCancel={() => {
        setDesignInput(initialDesignInput);
        onCancel?.();
      }}
      onSave={title => {
        designInput.title = title;
        if (designInput.id) {
          updateProductDesignInput(designInput);
        } else {
          createProductDesignInput(designInput);
        }
      }}
    />
  );
};

export default ProductBoardDesignInput;
